/**
 * Red de seguridad del arranque: los fallos que ocurren ANTES de que exista el
 * logger.
 *
 * El problema que resuelve. `index.tsx` instalaba `window.onerror` en el cuerpo
 * del módulo, y el cuerpo de un módulo ES corre DESPUÉS de que se hayan
 * evaluado todos sus imports. Para cuando esa línea se ejecutaba ya se habían
 * evaluado App, el logger, `services/supabase` con su `createClient()`… todo el
 * grafo. Si algo de eso reventaba al evaluarse, no había ningún manejador
 * puesto: el error subía al vacío, la app no arrancaba y el usuario veía una
 * página en blanco de la que nunca nos enterábamos.
 *
 * Este módulo se importa EL PRIMERO en `index.tsx` y no importa nada — ni el
 * logger, que es justo una de las piezas que puede no haber cargado. Instala
 * dos escuchas al evaluarse y guarda en una cola lo que llegue.
 *
 * CAZAR NO ES CONTAR. Guardar el fallo en una cola no sirve de nada si el único
 * modo de vaciarla es que alguien llame a `conectarLogger`, porque esa llamada
 * es una sentencia del cuerpo de `index.tsx` — o sea, del mismo grafo que acaba
 * de morir. En el caso exacto para el que existe esta red (revienta un módulo
 * al evaluarse) `conectarLogger` NO se llega a llamar nunca y la cola se queda
 * ahí. Por eso hay dos salidas:
 *
 *   1. La normal. `index.tsx` llama a `conectarLogger(reportFailure)`, la cola
 *      se vacía hacia el logger de verdad —con su huella, sus migas y su
 *      deduplicación— y a partir de ahí todo pasa derecho.
 *   2. La de emergencia. Si a los `ESPERA_LOGGER_MS` de haber cazado el primer
 *      fallo nadie ha conectado el logger, se da el grafo por muerto y la cola
 *      sale por `fetch` crudo contra el RPC `report_error`, con la URL y la
 *      llave publishable que Vite incrusta al compilar (`import.meta.env` es
 *      texto sustituido, no un import). El RPC está concedido a `anon`, así que
 *      no hace falta sesión. Cero imports, cero dependencia del grafo: es lo
 *      único que puede funcionar cuando no funciona nada más.
 *
 * Lo que la salida de emergencia NO trae, porque vive en el logger y el logger
 * es justo lo que no cargó: migas, correo del usuario, `scrubSecrets`. De ahí
 * que sólo se mande la RUTA (`pathname`) y nunca `location.href` — el `?t=` de
 * una propuesta pública es una credencial, y aquí no hay quien la limpie.
 *
 * Lo que NO alcanza: el código de las librerías de terceros (los trozos
 * `vendor-*`) se evalúa antes que el cuerpo del trozo de entrada, así que un
 * fallo dentro de React o de supabase-js al cargarse sigue quedando fuera; y si
 * el trozo de entrada no llega ni a descargarse, aquí no se evalúa nada. Para
 * cubrir también eso haría falta un `<script>` en `index.html`, que es otro
 * archivo.
 *
 * Se mantiene deliberadamente tonto. Un manejador que sobrevive vale más que
 * uno listo que añade otra forma de fallar.
 */

/** La forma de `reportFailure` (utils/logger), declarada aquí a mano para NO
 *  importarlo. */
type Reportador = (error: unknown, info?: { component?: string; context?: string }) => unknown;

interface FalloDeArranque {
    error: unknown;
    component: string;
    context: string;
}

/** Tope de la cola. Un arranque roto puede disparar el mismo error en bucle;
 *  con los primeros basta para saber qué pasó. */
const TOPE_COLA = 20;

/** Cuánto se le espera al logger antes de dar el grafo por muerto y mandar la
 *  cola por `fetch`.
 *
 *  El plazo arranca al cazar el PRIMER fallo, no al evaluar el módulo: si no
 *  pasa nada malo no hay temporizador. Tres segundos son una eternidad para lo
 *  que separa a este módulo de `conectarLogger` —el resto del trozo de entrada,
 *  todo síncrono— y siguen siendo poco para que el usuario cierre la pestaña en
 *  blanco. */
const ESPERA_LOGGER_MS = 3000;

/** Tope de envíos directos. Sin él, un `fetch` que falla levanta un
 *  'unhandledrejection', que entra aquí, que dispara otro `fetch`… */
const TOPE_ENVIO_DIRECTO = TOPE_COLA + 1;

const cola: FalloDeArranque[] = [];
let reportar: Reportador | null = null;
let descartados = 0;
let plazo: ReturnType<typeof setTimeout> | null = null;
let modoDirecto = false;
let enviadosDirecto = 0;

/**
 * Lo mismo que descarta utils/logger (EXTENSION_STACK_PATTERNS y
 * BOT_UA_PATTERN), copiado a propósito: importarlo sería importar el grafo que
 * puede no existir. Un bot corriendo el bundle dentro de happy-dom coló un
 * «vd is not a constructor» por esta puerta el 9-sep-2026, con la pila en
 * `file:///home/gary/…/node_modules/happy-dom/`.
 *
 * Sólo el marco de happy-dom/jsdom, no `file://` a secas como hace el logger:
 * en Node las pilas SIEMPRE son URLs `file://`, así que ese patrón se comería
 * todos los errores de las pruebas de este mismo archivo.
 */
const esRuidoDeArranque = (fallo: FalloDeArranque): boolean => {
    const pila = pilaDe(fallo.error) ?? '';
    if (/node_modules[\\/](happy-dom|jsdom)[\\/]/.test(pila)) return true;
    if (typeof navigator !== 'undefined'
        && /bot|crawler|spider|slurp|ahrefs|semrush|bingpreview|googlebot|facebookexternalhit|whatsapp|telegrambot|linkedinbot|discordbot|twitterbot|applebot|petalbot|yandex|baiduspider|duckduckbot/i.test(navigator.userAgent || '')) {
        return true;
    }
    return false;
};

function despachar(fallo: FalloDeArranque): void {
    if (esRuidoDeArranque(fallo)) return;
    if (reportar) {
        entregar(fallo);
        return;
    }
    // El plazo venció y el logger nunca apareció: lo que llegue va derecho a la
    // red, sin pasar por una cola que ya nadie va a vaciar.
    if (modoDirecto) {
        postear(fallo);
        return;
    }
    if (cola.length < TOPE_COLA) cola.push(fallo);
    else descartados += 1;
    armarPlazo();
}

function entregar(fallo: FalloDeArranque): void {
    if (!reportar) return;
    try {
        const r = reportar(fallo.error, { component: fallo.component, context: fallo.context });
        // `reportFailure` es async. Si su promesa se rechaza y nadie la mira,
        // el rechazo vuelve por 'unhandledrejection' — o sea, aquí — y se monta
        // el bucle. Se traga.
        if (r && typeof (r as Promise<unknown>).catch === 'function') {
            (r as Promise<unknown>).catch(() => { /* callado a propósito */ });
        }
    } catch { /* si el propio logger revienta, no hay nada mejor que hacer */ }
}

/* ------------------------------------------------------------------
 *  Salida de emergencia: el grafo no cargó, se manda a mano
 * ------------------------------------------------------------------ */

function armarPlazo(): void {
    if (plazo !== null || modoDirecto) return;
    if (typeof setTimeout !== 'function') return;
    plazo = setTimeout(vencePlazo, ESPERA_LOGGER_MS);
    // En Node (pruebas) un temporizador vivo retiene el proceso. En el navegador
    // `unref` no existe y esto no hace nada.
    const t = plazo as unknown as { unref?: () => void };
    if (typeof t.unref === 'function') t.unref();
}

function vencePlazo(): void {
    plazo = null;
    if (reportar) return; // llegó a tiempo; la cola ya se vació hacia el logger
    modoDirecto = true;

    const pendientes = cola.splice(0, cola.length);
    for (const fallo of pendientes) postear(fallo);

    if (descartados > 0) {
        const n = descartados;
        descartados = 0;
        postear({
            error: `Red de arranque: ${n} fallo(s) descartado(s) por tope de cola`,
            component: 'redDeArranque',
            context: `Se guardaron los primeros ${TOPE_COLA}`,
        });
    }
}

const textoDe = (error: unknown): string => {
    if (error instanceof Error) return error.message || error.name;
    if (typeof error === 'string') return error;
    try { return JSON.stringify(error) ?? String(error); }
    catch { return String(error); }
};

const pilaDe = (error: unknown): string | null =>
    (error instanceof Error && typeof error.stack === 'string') ? error.stack : null;

/** El mismo djb2 que usa utils/logger, copiado a propósito: importarlo sería
 *  importar el módulo que puede no existir. El prefijo deja a la vista en el
 *  panel que esa fila salió por la puerta de atrás. */
const huella = (component: string, mensaje: string): string => {
    const texto = `${component}|${mensaje.replace(/\d+/g, '#').slice(0, 300)}`;
    let hash = 5381;
    for (let i = 0; i < texto.length; i++) {
        hash = ((hash << 5) + hash + texto.charCodeAt(i)) | 0;
    }
    return `arranque-${(hash >>> 0).toString(16).padStart(8, '0')}`;
};

const idDeBuild = (): string => {
    try { return typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'dev'; }
    catch { return 'dev'; }
};

const espacioActivo = (): string | null => {
    try { return typeof localStorage !== 'undefined' ? localStorage.getItem('2bros_active_workspace') : null; }
    catch { return null; }
};

function postear(fallo: FalloDeArranque): void {
    if (enviadosDirecto >= TOPE_ENVIO_DIRECTO) return;
    enviadosDirecto += 1;
    try {
        // Vite sustituye estas dos expresiones por su texto al compilar, así que
        // no hay ningún módulo detrás de ellas.
        const url = String(import.meta.env.VITE_SUPABASE_URL ?? '').trim().replace(/\/+$/, '');
        const llave = String(import.meta.env.VITE_SUPABASE_ANON_KEY ?? '').trim();
        if (!url || !llave) return;
        if (typeof fetch !== 'function') return;

        const mensaje = textoDe(fallo.error);
        const pila = pilaDe(fallo.error);

        const respuesta = fetch(`${url}/rest/v1/rpc/report_error`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                apikey: llave,
                Authorization: `Bearer ${llave}`,
            },
            // La pestaña en blanco es una pestaña que el usuario cierra. Con
            // `keepalive` la petición sobrevive al cierre; el cuerpo es de sobra
            // menor que los 64 KB que ese modo permite.
            keepalive: true,
            body: JSON.stringify({
                p_message: mensaje.slice(0, 2000),
                p_stack: pila ? pila.slice(0, 8000) : null,
                p_user_email: null,
                p_severity: 'error',
                p_fingerprint: huella(fallo.component, mensaje),
                p_context: {
                    component: fallo.component,
                    detail: fallo.context,
                    // Sin `location.href`: el query string puede llevar el token
                    // de una propuesta pública y aquí no hay `scrubSecrets`.
                    browser: typeof navigator !== 'undefined' ? navigator.userAgent : null,
                    transporte: 'redDeArranque/fetch',
                    aviso: 'El grafo de módulos no llegó a cargar; informe enviado sin logger.',
                },
                p_route: typeof window !== 'undefined' ? (window.location?.pathname || '/') : null,
                p_workspace_id: espacioActivo(),
                p_build_id: idDeBuild(),
                p_viewport: null,
                p_breadcrumbs: null,
                p_increment: 1,
            }),
        });
        // Si la red también falla, que el rechazo no vuelva por
        // 'unhandledrejection' y se muerda la cola.
        if (respuesta && typeof respuesta.catch === 'function') {
            respuesta.catch(() => { /* callado a propósito */ });
        }
    } catch { /* no queda nadie a quien contárselo */ }
}

const alError = (ev: ErrorEvent): void => {
    despachar({
        error: ev.error ?? ev.message,
        component: 'window.onerror',
        context: `Source: ${ev.filename} (${ev.lineno}:${ev.colno})`,
    });
};

const alRechazo = (ev: PromiseRejectionEvent): void => {
    despachar({
        error: ev.reason,
        component: 'unhandledrejection',
        context: 'Unhandled promise rejection',
    });
};

/**
 * Entrega al logger de verdad lo que se haya acumulado y le pasa el testigo.
 * Se llama desde `index.tsx` en cuanto el grafo está cargado.
 */
export function conectarLogger(fn: Reportador): void {
    reportar = fn;
    // Llegó a tiempo: se desarma la salida de emergencia.
    if (plazo !== null) {
        clearTimeout(plazo);
        plazo = null;
    }
    const pendientes = cola.splice(0, cola.length);
    for (const fallo of pendientes) entregar(fallo);
    if (descartados > 0) {
        const n = descartados;
        descartados = 0;
        entregar({
            error: `Red de arranque: ${n} fallo(s) descartado(s) por tope de cola`,
            component: 'redDeArranque',
            context: `Se guardaron los primeros ${TOPE_COLA}`,
        });
    }
}

// El efecto: se instalan al evaluar el módulo, que es lo único que hace que
// esto sirva para algo.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('error', alError);
    window.addEventListener('unhandledrejection', alRechazo);
}

/** Sólo para las pruebas. No se usa en tiempo de ejecución. */
export const __pruebas = {
    alError,
    alRechazo,
    cola,
    TOPE_COLA,
    ESPERA_LOGGER_MS,
    reiniciar() {
        cola.length = 0;
        reportar = null;
        descartados = 0;
        if (plazo !== null) { clearTimeout(plazo); plazo = null; }
        modoDirecto = false;
        enviadosDirecto = 0;
    },
};
